import { Badge } from "./Badge";
import type { BadgeKind } from "./Badge";
import { ButtonLink } from "./Button";
import { ReportDealForm } from "./ReportDealForm";
import { VisuallyHidden } from "./VisuallyHidden";
import { trackedDealUrl } from "../lib/click";
import { formatMoney, formatPercentOff } from "../lib/format";
import { displayHost } from "../lib/url";
import styles from "./DealEntry.module.css";
import type { BoardEntry } from "../api/types";

/**
 * One deal as it appears on the board — the canvas's row, exactly.
 *
 * Four columns: rank, identity, value, action. The identity column is the product name with
 * its verified mark and badges on one line, `host · value` clipped to a second, and the
 * description on a third of its own. The value column is the price with the original struck
 * through and the discount beside it. Nothing else is on the row.
 *
 * `WEB-046` is what made that three lines rather than two. The canvas draws one clipped
 * `seller · description` line and the build matched it; the operator, reading a real board,
 * found the description was the part the ellipsis ate. So the seller's name comes off the
 * row entirely and the description gets a line it does not share.
 *
 * The canvas tiers the first three positions and leaves 4–10 compact, because rank is the
 * entire outcome of the auction that produced this page. The weight goes on the rank mark
 * and the price — the two things canon ranks first and second — and never on a claim about
 * the deal, because canon § 12 forbids presenting a high bid as an editorial judgement.
 *
 * What the canvas defers to "the deal's expanded view" — coupon code, limitations, and how
 * the position was obtained — lives behind the row's `View more` control. V0 has no deal
 * detail page to defer to, and canon § 14 wants how a position was filled stated on every
 * entry including the free ones, so it is folded rather than dropped. It is a chip on the
 * end of the identity line rather than a row of its own: a summary line under all ten rows
 * was the single biggest thing separating the built board from the canvas.
 *
 * `WEB-009` renamed the control and gave it a chevron; it moved nothing. What is behind it
 * is what `WEB-007` put there, the per-row `Sponsored` badge included, and the disclosure
 * canon § 12 actually turns on — the board's brass band above rank 01 — was never in here
 * and is not now.
 */

const SOURCE_NOTE: Record<BoardEntry["source"], string> = {
  auction: "Sponsored placement, won in yesterday's auction",
  rollover: "Held over to fill the board — no additional charge",
  founding: "Founding Deal placement while the board is filling",
  honorable: "Paid one-day listing outside the Top 10",
};

/** Canon § 4 badge codes to the design system's badge kinds. */
const BADGE_KIND: Record<string, BadgeKind> = {
  founding: "founding",
  ltd: "ltd",
  exclusive: "exclusive",
};

/**
 * How much visual weight a position gets. 1, 2 and 3 are the canvas's stepped tiers; 4 is
 * every remaining position and the default.
 */
export type RankTier = 1 | 2 | 3 | 4;

/** The tier a rank belongs to. Ranks below 1 or above 3 are all the compact tier. */
export function tierForRank(rank: number | null): RankTier {
  if (rank === 1) return 1;
  if (rank === 2) return 2;
  if (rank === 3) return 3;
  return 4;
}

export function DealEntry({
  entry,
  headingLevel = 3,
  tier,
}: {
  entry: BoardEntry;
  /**
   * Heading level for the product name, so the entry nests correctly under whichever
   * section owns it. The Top 10's own heading is the page's `h1`, which would otherwise
   * leave these entries skipping a level — a real navigation defect for a screen-reader
   * user, not a lint nit.
   */
  headingLevel?: 2 | 3 | undefined;
  /**
   * Visual weight for this position. Defaults to the tier the entry's own rank belongs to,
   * so a board cannot end up with ten rows that all forgot to ask.
   */
  tier?: RankTier | undefined;
}) {
  const weight = tier ?? tierForRank(entry.rank);
  const Product = headingLevel === 2 ? "h2" : "h3";
  const { deal } = entry;
  const host = displayHost(deal.productUrl) ?? displayHost(deal.dealUrl);
  const sponsored = entry.source === "auction" || entry.source === "honorable";
  const isMention = entry.source === "honorable";
  // Canon § 11: the call to action goes through core's tracked endpoint so the click can
  // count towards the weekly and monthly lists. The surface follows the entry rather than
  // the section that rendered it — an Honorable Mention is a mention wherever it appears.
  const ctaUrl = trackedDealUrl(deal.id, isMention ? "honorable" : "daily_board", deal.dealUrl);

  const badgeKinds = deal.badges.flatMap((code) => {
    const kind = BADGE_KIND[code];
    return kind === undefined ? [] : [kind];
  });

  const hasPrices = deal.originalPriceCents !== null && deal.dealPriceCents !== null;

  return (
    <article className={`${styles.row} ${styles[`tier${String(weight)}`]}`}>
      {entry.rank === null ? null : (
        <p className={`${styles.rank} figure`} aria-hidden="true">
          {String(entry.rank).padStart(2, "0")}
        </p>
      )}

      <div className={styles.identity}>
        <div className={styles.titleLine}>
          <Product className={styles.product}>
            {entry.rank === null ? null : (
              <VisuallyHidden>Rank {String(entry.rank)}: </VisuallyHidden>
            )}
            {deal.productName}
          </Product>
          {deal.verified ? <Badge kind="verified" /> : null}
          {badgeKinds.map((kind) => (
            <Badge key={kind} kind={kind} />
          ))}
        </div>

        {/*
          Host and the written value, clipped to one line — the canvas's full-page row puts
          the value note here rather than in the price column, and this is what is left of
          its byline once `WEB-046` took two things out of it.

          The seller's display name is **gone**, not merely reordered. Canon § 3 makes the
          submitter's association with the product a verification fact checked before
          approval, and `Deal Verified` is how that fact reaches a buyer; canon § 13 lists
          what a buyer can see and names badges and deal terms, not the submitter. Printing
          it ahead of the description meant the ellipsis ate the description first, on the
          row where a buyer is deciding whether to care.
        */}
        {host === null && deal.valueNotes === null ? null : (
          <p className={styles.meta}>
            {host === null ? null : <span>{host}</span>}
            {host !== null && deal.valueNotes !== null ? (
              <span aria-hidden="true"> · </span>
            ) : null}
            {deal.valueNotes === null ? null : <span>{deal.valueNotes}</span>}
          </p>
        )}

        {/*
          The description, on a line of its own (`WEB-046`).

          Bounded by a line clamp rather than by cutting the string. `shortDescription` is
          already capped at 400 characters server-side — `dealbid-core`'s
          `src/domain/submission.ts` and `src/domain/deal-edit.ts`, on submit and on edit —
          so a second, shorter cut here would print a description on the board that differs
          from the one in the founder's own desk, for a limit the server already enforces.
          Clamping leaves the whole sentence in the DOM: a screen reader reads it, and a
          buyer can select and copy it.
        */}
        <p className={styles.description}>{deal.shortDescription}</p>
      </div>

      <div className={styles.value}>
        {hasPrices ? (
          <>
            <p className={`${styles.now} figure`}>
              <VisuallyHidden>Deal price </VisuallyHidden>
              {formatMoney(deal.dealPriceCents ?? 0)}
            </p>
            <p className={styles.priceRow}>
              <VisuallyHidden>Usually </VisuallyHidden>
              <s className={`${styles.was} figure`}>
                {formatMoney(deal.originalPriceCents ?? 0)}
              </s>
              {deal.discountPercent === null ? null : (
                <span className={`${styles.off} figure`}>
                  {formatPercentOff(deal.discountPercent)}
                </span>
              )}
            </p>
          </>
        ) : null}
      </div>

      <div className={styles.action}>
        {ctaUrl === null ? (
          // docs/SECURITY.md § URLs: only http/https destinations are ever linked. Rather
          // than render a dead or unsafe control, the entry keeps its information and drops
          // the action — core would refuse to redirect there, so the link would be dead and
          // the click record would describe a visit that never lands.
          <p className={styles.unavailable}>Link unavailable</p>
        ) : (
          <ButtonLink
            className={styles.cta}
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get this deal
            <VisuallyHidden> from {deal.productName} (opens in a new tab)</VisuallyHidden>
          </ButtonLink>
        )}
      </div>

      {/*
        Canon § 12 and § 14: how the position was obtained is stated on every entry, the free
        rollover and founding ones included — saying it only on the sponsored rows would turn
        a fact about how the board works into a label some deals wear. Folded, because the
        canvas's row does not carry it; reachable by keyboard, and costing the row no height.
      */}
      <details className={styles.details}>
        {/*
          `WEB-008`–`WEB-013` round: the chip used to read "Terms", which named what was
          inside it but not what the control did — nothing on the row told a reader it
          opened. It now names the action and draws the chevron that says so. The chevron is
          `aria-hidden`: `details`/`summary` already exposes the expanded state, and a glyph
          that repeated it would announce the control twice.
        */}
        <summary className={styles.summary}>
          View more
          <VisuallyHidden> about {deal.productName}</VisuallyHidden>
          <svg
            className={styles.chevron}
            viewBox="0 0 10 6"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M1 1l4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </summary>
        <div className={styles.panel}>
          <p className={styles.source}>
            {SOURCE_NOTE[entry.source]}
            {entry.winningBidCents === null ? null : (
              <>
                {" · "}
                <span className="figure">{formatMoney(entry.winningBidCents)}</span> winning
                bid
              </>
            )}
            {sponsored ? (
              <>
                {" "}
                <Badge kind="sponsored" />
              </>
            ) : null}
          </p>
          <dl className={styles.terms}>
            {deal.couponCode === null ? null : (
              <div className={styles.term}>
                <dt>Code</dt>
                <dd className="figure">{deal.couponCode}</dd>
              </div>
            )}
            {deal.limitations === null ? null : (
              <div className={styles.term}>
                <dt>Limitations</dt>
                <dd>{deal.limitations}</dd>
              </div>
            )}
          </dl>
          <ReportDealForm dealId={deal.id} productName={deal.productName} />
        </div>
      </details>
    </article>
  );
}
